import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxPopoverModule,
  DxPopupModule,
  DxToolbarModule,
} from 'devextreme-angular';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { UtilsService } from '../../../services/utils.service';
import { DataCoreService } from '../../../services/data-core.service';
import { OrtakService } from '../../../services/ortak.service';
import {
  HistoryComponent,
  HistoryFilterData,
} from '../../history/history.component';
// DKM-1078 Cedric: Yardım → Destek Talebi Aç
import { DkFormPopupComponent } from '../../form-popup/dk-form-popup.component';
import { DkListHelpFormComponent } from '../../help-desk/dk-list-help-form/dk-list-help-form.component';
import { EkranHataKaydi, ekranHataKaydi } from '../../../services/ekran-hata-kaydi'; // DKM-5193 Cedric

export interface FormFooterAuditInfo {
  kayitTarih?: Date;
  degisTarih?: Date;
  kayitKullaniciAd?: string;
  degisKullaniciAd?: string;
}

@Component({
  selector: 'dk-form-footer',
  standalone: true,
  imports: [
    CommonModule,
    DxButtonModule,
    DxToolbarModule,
    DxiItemModule,
    DxPopoverModule,
    DxPopupModule,
    HistoryComponent,
    // DKM-1078 Cedric
    DkFormPopupComponent,
    DkListHelpFormComponent,
  ],
  templateUrl: './form-footer.component.html',
  styleUrls: ['./form-footer.component.scss'],
})
export class FormFooterComponent implements OnChanges {
  @Input() formName!: string;
  @Input() historyEntityName!: string;
  @Input() historyEntityData!: HistoryFilterData;
  /** Formun zaten [(formData)] ile bağlı olan ana kayıt objesi. Verildiğinde
   *  historyEntityData kurmaya gerek kalmadan KayitTarih/KayitKullaniciKod/DegisTarih/
   *  DegisKullaniciKod alanları doğrudan bu objeden okunur. */
  @Input() recordData: any;
  @Input() state: string;
  @Input() formAction: string = '';
  @Input() errorResponse: string[];
  @Input() isInDrawer: boolean = false;

  @Output() showHistory = new EventEmitter<void>();
  historyPopupVisible = false;
  auditInfo: FormFooterAuditInfo | null = null;

  toastMessageButtonOptions: DxButtonTypes.Properties = {
    icon: 'belloutline',
    stylingMode: 'outlined',
    hint: 'Hata Mesajları',
    onClick: () => {
      // DKM-5193 Cedric: eski hâli — yalnız ekran servisinin tek errorResponse alanını gösteriyordu
      // this.showLastErr(this.errorResponse);
      this.hataMesajlariniGoster();
    },
  };
  histyoryButtonOptions: DxButtonTypes.Properties = {
    icon: 'eyeopen',
    stylingMode: 'outlined',
    hint: 'İşlem Geçmişi',
    onClick: () => {
      this.showCrudHistory();
    },
  };

  // DKM-1078 Cedric: liste footer'daki "Yardım" ile aynı — Destek Talebi Aç popup'ı
  helpPopupVisible = false;
  helpButtonOptions: DxButtonTypes.Properties = {
    icon: 'comment',
    stylingMode: 'outlined',
    hint: 'Yardım',
    onClick: () => {
      this.helpPopupVisible = true;
    },
  };

  infoButtonOptions: DxButtonTypes.Properties = {
    icon: 'help',
    hint: 'Uyarılar',
    stylingMode: 'outlined',
    onClick: () => {},
  };

  constructor(
    private utilsService: UtilsService,
    private dataCoreService: DataCoreService,
    private ortakService: OrtakService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['historyEntityData'] || changes['historyEntityName'] || changes['state'] || changes['recordData']) {
      this.loadAuditInfo();
    }
    // dtokgoz 17.10: açık kayıt hata kaydına (destek talebi) girsin — kayıt hata anında okunur. errorResponse
    // değişimi sıralamayı bozmasın diye yalnız kayıt değişince (ya da ilk kez) bildirilir.
    if (changes['recordData'] || changes['historyEntityData'] || !this.kayitBildirildi) {
      this.kayitBildirildi = true;
      ekranHataKaydi.kayitKaynagiBildir(this, () => ({
        form: this.formName,
        kayit: this.recordData ?? (this.historyEntityData?.Kod ? { Kod: this.historyEntityData.Kod } : null),
      }));
    }
  }

  private kayitBildirildi = false;

  ngOnDestroy() {
    ekranHataKaydi.kayitKaynagiBirak(this); // dtokgoz 17.10
  }

  /** historyEntityData verilmemişse, [(formData)] ile zaten bağlı olan recordData'dan
   *  aynı şekle (HistoryFilterData) sahip bir obje üretir. */
  get effectiveEntityData(): HistoryFilterData | null {
    if (this.historyEntityData) {
      return this.historyEntityData;
    }
    if (this.recordData?.Kod) {
      return {
        Kod: this.recordData.Kod,
        kayitAdi: this.historyEntityName,
        kaydedenKod: this.recordData.KayitKullaniciKod,
        kayitTarih: this.recordData.KayitTarih,
        sonDegistirenKod: this.recordData.DegisKullaniciKod,
        degisiklikTarih: this.recordData.DegisTarih,
      } as HistoryFilterData;
    }
    return null;
  }

  private loadAuditInfo(): void {
    this.auditInfo = null;
    const data = this.effectiveEntityData;
    if (this.state === 'New' || !data?.Kod) {
      return;
    }

    // Ana kayıt formu açılırken zaten yüklenmiş oluyor (recordData ya da setHistoryFilterData()
    // ile FormData.KayitTarih/KayitKullaniciKod/... zaten elimizde). Böyle bir durumda tekrar
    // sorgu atmıyoruz: historyEntityName her zaman gerçek bir OData entity'sine karşılık
    // gelmeyebiliyor (form kodu da olabiliyor), bu da gereksiz/başarısız isteklere yol açıyor.
    if (data.kayitTarih || data.kaydedenKod || data.sonDegistirenKod || data.degisiklikTarih) {
      this.applyAuditInfo(data.kaydedenKod, data.kayitTarih, data.sonDegistirenKod, data.degisiklikTarih);
      return;
    }

    // Elimizde hazır veri yoksa, sadece tablolarda (view'lerde değil) bulunan
    // KayitTarih/KayitKullaniciKod/DegisTarih/DegisKullaniciKod alanlarını tek seferlik
    // OData sorgusuyla çekmeyi dene; entity uyuşmazsa sessizce vazgeç.
    const entityName = this.historyEntityName || data.kayitAdi;
    if (!entityName || entityName.startsWith('v')) {
      return;
    }
    const store = this.dataCoreService.getOData(entityName);
    store.select(['KayitKullaniciKod', 'KayitTarih', 'DegisKullaniciKod', 'DegisTarih']);
    store.filter(['Kod', '=', data.Kod]);
    store
      .load()
      .then((res: any) => {
        const row = res?.[0];
        this.applyAuditInfo(
          row?.KayitKullaniciKod,
          row?.KayitTarih,
          row?.DegisKullaniciKod,
          row?.DegisTarih
        );
      })
      .catch(() => {
        this.auditInfo = {};
      });
  }

  private applyAuditInfo(
    kayitKullaniciKod: number,
    kayitTarih: Date,
    degisKullaniciKod: number,
    degisTarih: Date
  ): void {
    this.auditInfo = { kayitTarih, degisTarih };
    if (kayitKullaniciKod > 0 || degisKullaniciKod > 0) {
      this.ortakService
        .getKullaniciAd(kayitKullaniciKod || 0, degisKullaniciKod || 0)
        .subscribe((res) => {
          if (this.auditInfo) {
            this.auditInfo.kayitKullaniciAd = res?.kayitKullaniciAd;
            this.auditInfo.degisKullaniciAd = res?.degisKullaniciAd;
          }
        });
    }
  }

  showLastErr(errorRes: any) {
    let err = errorRes?.message ? JSON.parse(errorRes?.message) : errorRes;
    if (
      err === null ||
      err === undefined ||
      (typeof err !== 'object' && err.length === 0) ||
      (typeof err === 'object' && Object.keys(err).length === 0) ||
      err === 0 ||
      err === ''
    ) {
      this.utilsService.showSuccess(
        'Hata Mesajı Bulunmuyor',
        '',
        'toast-bottom-right'
      );
    } else {
      this.utilsService.showError(err, '', 'toast-bottom-right');
    }
  }

  /**
   * DKM-5193 Cedric: "Hata Mesajları" — bu sekmede alınan son 10 hata (ekran hata kaydı; form popup'ı da açıldığı
   * sekmeye aittir). Kayıt yoksa eski davranış: errorResponse aynen showLastErr ile gösterilir (o gösterim yeni kayıt açmaz).
   */
  hataMesajlariniGoster() {
    let kayitlar: EkranHataKaydi[] = [];
    try {
      kayitlar = ekranHataKaydi.liste(ekranHataKaydi.aktifSekme(), 10);
    } catch {
      kayitlar = [];
    }
    if (!kayitlar.length) {
      ekranHataKaydi.kaydetmeden(() => this.showLastErr(this.errorResponse));
      return;
    }
    const ad = this.formName ? this.getFormName(this.formName) : '';
    this.utilsService.showHataListesi(
      `Bu ekranda alınan son hatalar (${kayitlar.length})${ad ? ' — ' + ad : ''}`,
      ekranHataKaydi.listeHtml(kayitlar)
    );
  }

  showCrudHistory() {
    if (this.effectiveEntityData) {
      this.historyPopupVisible = true;
    }
  }
  getFormName(page: any): string {
    return page?.slice(page?.lastIndexOf('/') + 1);
  }
}
